import { useState, useCallback } from 'react';
import axios from 'axios';
import { Auth } from 'aws-amplify';
import { APP_ID, R05_API_CONFIG } from '../utils/Constants';
import { useFujiAuth } from '../auth/useFujiAuth';
import { useNavigate } from 'react-router-dom';
import { store } from '../redux/store';
import { showNotificationDialog } from '../utils';
import { useTranslation } from 'react-i18next';
import {
    Calendar,
    DistributionQrCode,
    Holidays,
    OrderTicket,
    OrderTicketList,
    PurchaseResponse,
    SelectNumber,
    Stocks,
    Stops,
    TicketInfo,
} from 'types';
import { Distributions } from 'types/distributions';

export interface ApiError extends Error {
    errorType?: string;
    isTimeout?: boolean;
    status?: number;
}
const instance = axios.create({
    baseURL: R05_API_CONFIG.baseUrl,
    headers: {
        'x-api-key': R05_API_CONFIG.API_KEY,
    },
});

instance.interceptors.request.use(async (config) => {
    try {
        await Auth.currentAuthenticatedUser({ bypassCache: true });
        // refresh token
        const session = await Auth.currentSession();
        config.headers.Authorization = 'Bearer ' + session.getIdToken().getJwtToken();
    } catch (error) {
        console.error(error);
    }
    return config;
});

instance.interceptors.response.use(
    function (response) {
        const { data } = response;
        return data;
    },
    function (error) {
        const { data, status } = error?.response ?? {};
        if (status === 503) {
            store.dispatch({
                type: 'SERVER_MAINTENANCE',
                payload: {
                    isMaintenance: true,
                    endAt: data?.endAt,
                },
            });
        } else {
            const responseError = new Error() as ApiError;
            responseError.errorType = data?.errorType ?? 'unexpected';
            responseError.message = data?.message;
            responseError.isTimeout = axios.isCancel(error);
            responseError.status = status;
            return Promise.reject(responseError);
        }
    },
);

const useR05Api = () => {
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { isAuthenticated } = useFujiAuth();
    const { t } = useTranslation();

    /**
     * API(GET)を叩く！
     */
    const hittingGetApi = useCallback(async (url: string, params: any, timeout = 0) => {
        setLoading(true);
        const controller = new AbortController();
        let timeoutId;
        try {
            if (typeof timeout === 'number' && timeout > 0) {
                timeoutId = setTimeout(() => {
                    controller.abort();
                }, timeout);
            }
            const response = await instance.get(url, { params: { ...params }, signal: controller.signal });
            setLoading(false);
            clearTimeout(timeoutId);
            return response;
        } catch (error: any) {
            setLoading(false);
            clearTimeout(timeoutId);
            if (error.status === 504) {
                showNotificationDialog('error', '', t('notification.title'), t('error-message.concentrated-access'));
            } else {
                throw error;
            }
        }
    }, []);

    /**
     * API(POST)を叩く！
     */
    const hittingPostApi = useCallback(
        async (url: string, params: any, timeout = 0) => {
            setLoading(true);
            const controller = new AbortController();
            let timeoutId;
            try {
                if (typeof timeout === 'number' && timeout > 0) {
                    timeoutId = setTimeout(() => {
                        controller.abort();
                    }, timeout);
                }
                const response = await instance.post(url, params, { signal: controller.signal });
                setLoading(false);
                clearTimeout(timeoutId);
                return response;
            } catch (error: any) {
                setLoading(false);
                clearTimeout(timeoutId);
                if (!isAuthenticated) {
                    navigate('/buy');
                }
                if (error.status === 504) {
                    showNotificationDialog(
                        'error',
                        '',
                        t('notification.title'),
                        t('error-message.concentrated-access'),
                    );
                } else {
                    throw error;
                }
            }
        },
        [isAuthenticated, navigate],
    );

    /**
     * API(PUT)を叩く！
     */
    const hittingPutApi = useCallback(
        async (url: string, params: any, timeout = 0) => {
            setLoading(true);
            const controller = new AbortController();
            let timeoutId;
            try {
                if (typeof timeout === 'number' && timeout > 0) {
                    timeoutId = setTimeout(() => {
                        controller.abort();
                    }, timeout);
                }
                const response = await instance.put(url, params, { signal: controller.signal });
                setLoading(false);
                clearTimeout(timeoutId);
                return response;
            } catch (error: any) {
                setLoading(false);
                clearTimeout(timeoutId);
                if (!isAuthenticated) {
                    navigate('/buy');
                }
                if (error.status === 504) {
                    showNotificationDialog(
                        'error',
                        '',
                        t('notification.title'),
                        t('error-message.concentrated-access'),
                    );
                } else {
                    throw error;
                }
            }
        },
        [isAuthenticated, navigate],
    );

    /**
     * API(DELETE)を叩く！
     */
    const hittingDeleteApi = useCallback(async (url: string, params: any, timeout = 0) => {
        setLoading(true);
        const controller = new AbortController();
        let timeoutId;
        try {
            if (typeof timeout === 'number' && timeout > 0) {
                timeoutId = setTimeout(() => {
                    controller.abort();
                }, timeout);
            }
            const response = await instance.delete(url, { params: { ...params }, signal: controller.signal });
            setLoading(false);
            clearTimeout(timeoutId);
            return response;
        } catch (error: any) {
            setLoading(false);
            clearTimeout(timeoutId);
            if (error.status === 504) {
                showNotificationDialog('error', '', t('notification.title'), t('error-message.concentrated-access'));
            } else {
                throw error;
            }
        }
    }, []);

    /**
     * アプリ情報取得API
     */
    const fetchAppConfig = useCallback(async () => hittingGetApi('/application', { appId: APP_ID }), [hittingGetApi]);

    /**
     * ###DUMMY### チケットグループ取得API
     * @param {Object} params -get request parameters
     * @param {string} params.appId -(optional)アプリID
     */
    const fetchGroupTicketList = useCallback(async () => {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return [];
    }, []);

    /**
     * チケット一覧取得API
     * @param {Object} params -get request parameters
     * @param {string} params.appId -(optional)アプリID
     * @param {string} params.saleTicketGroupId -(optional)販売券グループID
     */
    const fetchTicketList = useCallback(
        async (params: { appId: string; saleTicketGroupId?: string }) => {
            const response = await hittingGetApi('/tickets', { ...params, appId: APP_ID });
            return response as unknown as TicketInfo[];
        },
        [hittingGetApi],
    );

    /**
     * チケット詳細取得API
     * @param {Object} params -get request parameters
     * @param {string} params.salesTicketId -(required)販売券種ID
     * @returns
     */
    const fetchTicketInfo = useCallback(
        async (params: { salesTicketId: string }): Promise<TicketInfo | undefined> => {
            const response = await hittingGetApi('/ticket', params);
            return response as unknown as TicketInfo;
        },
        [hittingGetApi],
    );

    /**
     * 企画券購入API
     * @param {Object} params -get request parameters
     * @param {string} params.salesTicketId -(required)販売券種ID
     * @param {string} params.lang -(required)言語コード
     * @param {number} params.numberAdult -人数(大人)
     * @param {number} params.countCustom1 -人数(カスタム１)
     * @param {number} params.countCustom2 -人数(カスタム２)
     * @param {number} params.countCustom3 -人数(カスタム３)
     */
    const purchaseTicket = useCallback(
        async (params: {
            salesTicketId: number;
            lang: string;
            numberAdult?: number;
            countCustom1?: number;
            countCustom2?: number;
            countCustom3?: number;
        }) => {
            const response = await hittingPostApi('/ticket/purchase', params);
            return response as unknown as PurchaseResponse;
        },
        [hittingPostApi],
    );

    /**
     * 購入済みチケット一覧取得API
     * @param {Object} params -get request parameters
     * @param {string} params.status -(optional)ステータス。複数指定可能 orderIdと同時指定不可
     * @returns
     */
    const fetchOrderList = useCallback(
        async (params: { status?: string }) => {
            const response = await hittingGetApi('/orders', params);
            return response as unknown as OrderTicketList[];
        },
        [hittingGetApi],
    );

    /**
     * 購入済みチケット詳細取得API
     * @param {Object} params -get request parameters
     * @param {string} params.orderId -(required)オーダーID
     * @param {string} params.orderTicketId -(optional)子券ID
     * @returns
     */
    const fetchOrderDetail = useCallback(
        async (params: { orderId: string; orderTicketId?: string | null }) => {
            const response = await hittingGetApi('/order', { ...params });
            return response as unknown as OrderTicket;
        },
        [hittingGetApi],
    );
    /**
     * スポットコード確認API
     * @param {Object} params -post request parameters
     * @param {string} params.orderTicketId -(required) 子券ID
     * @param {string} params.appId -(optional) アプリID
     * @param {string} params.spotCode -(optional) 入力コード
     */
    const validateSpot = useCallback(
        async (params: { orderTicketId: string; appId?: string; spotCode?: string }) =>
            hittingPostApi('/order/spot/validate', { appId: APP_ID, ...params }),
        [hittingPostApi],
    );

    /**
     * チケット有効化API
     * @param {Object} params -put request parameters
     * @param {string} params.orderTicketId -(required) 子券ID
     */
    const activateTicket = useCallback(
        async (params: { orderTicketId: string; deviceId: string | null }) =>
            hittingPutApi('/order/status/activate', params),
        [hittingPutApi],
    );

    /**
     * スポットコード使用API
     * @param {Object} params -put request parameters
     * @param {string} params.spotId -(required) スポットID
     * @param {string} params.orderTicketId -(required) 子券ID
     * @param {number} params.usingNumber -(optional) 使用枚数
     */
    const usageSpotTicket = useCallback(
        async (params: { spotId: string; orderTicketId: string; usingNumber?: number }) =>
            hittingPutApi('/order/spot/status/activate', params),
        [hittingPutApi],
    );

    /**
     * チケット払い戻しAPI
     * @param {Object} params -post request parameters
     * @param {string} params.orderId -(required) 払戻し対象の企画券ID
     */
    const refundTicket = useCallback(
        async (params: { orderId: string; cancellationFee: number | null }) => {
            const response = await hittingPostApi('/order/refund', params);
            return response as unknown as { message: string[] };
        },
        [hittingPostApi],
    );

    /**,
     * クレジットカードでチケット購入API
     * @param {Object} params -get request parameters
     * @param {string} params.salesTicketId -(required)販売券種ID
     * @param {string} params.lang -(required)言語コード
     * @param {string} params.callbackUrl -支払い完了後リダイレクトリンク
     * @param {string} params.cardToken -カード情報を暗号化した
     * @param {string} params.payment_system -支払い方法
     * @param {number} params.numberAdult -人数(大人)
     * @param {number} params.countCustom1 -人数(カスタム１)
     * @param {number} params.countCustom2 -人数(カスタム２)
     * @param {number} params.countCustom3 -人数(カスタム３)
     * @param {Array}  params.reservationCombo -予約ありの個別チケットIDと在庫IDの組み合わせ
     * @param {Object} params.userForm -利用者情報の回答
     * @param {Object} params.voucherCode -バウチャーコード
     */
    const purchaseTicketWithCard = useCallback(
        async (params: {
            salesTicketId?: number;
            lang: string;
            callbackUrl?: string;
            cardToken?: string;
            payment_system?: string;
            numberAdult?: number;
            countCustom1?: number;
            countCustom2?: number;
            countCustom3?: number;
            reservationCombo?: Array<any>;
            voucherCode?: string;
        }) => {
            const response = await hittingPostApi('/ticket/purchase/card/start', params);
            return response as unknown as PurchaseResponse;
        },
        [hittingPostApi],
    );

    /**,
     * クレジットカードでチケット購入APIはまだ完了しない場合は決済apiを叩く必要がある
     * @param {Object} params -get request parameters
     * @param {string} params.accessId -3DS2認証後のコールバック accessId
     * @param {string} params.transactionId -3DS2認証後のコールバック csrfToken
     */
    const purchaseTicketWithCardComplete = useCallback(
        async (params: { accessId?: string; transactionId?: string }) =>
            hittingPostApi('/ticket/purchase/card/complete', params),
        [hittingPostApi],
    );

    /**,
     * PayPayでチケット購入API
     * @param {Object} params -get request parameters
     * @param {string} params.salesTicketId -(required)販売券種ID
     * @param {string} params.lang -(required)言語コード
     * @param {string} params.callbackUrl -支払い完了後リダイレクトリンク
     * @param {string} params.paymentType -支払い方法
     * @param {number} params.numberAdult -人数(大人)
     * @param {number} params.countCustom1 -人数(カスタム１)
     * @param {number} params.countCustom2 -人数(カスタム２)
     * @param {number} params.countCustom3 -人数(カスタム３)
     * @param {Object} params.voucherCode -バウチャーコード
     */
    const purchaseTicketWithPayPay = useCallback(
        async (params: {
            salesTicketId?: number;
            lang: string;
            callbackUrl?: string;
            paymentType?: string;
            numberAdult?: number;
            countCustom1?: number;
            countCustom2?: number;
            countCustom3?: number;
            reservationCombo?: Array<any>;
            voucherCode?: string;
        }) => {
            const response = hittingPostApi('/ticket/purchase/pay/start', params);
            return response as unknown as PurchaseResponse;
        },
        [hittingPostApi],
    );

    /**,
     * ApplePay認証API
     * @param {Object} params -get request parameters
     * @param {string} params.secretName -(required)シークレット名
     * @param {string} params.validationUrl -(required)バリデーションURL
     * @param {string} params.displayName -(required)表示名
     * @param {string} params.merchantIdentifier -(required)マーチャント識別子
     * @param {string} params.initiativeContext -(required)イニシアチブコンテキスト
     */
    const applePayAuth = useCallback(
        async (params: {
            secretName: string;
            validationUrl: string;
            displayName: string;
            merchantIdentifier: string;
            initiativeContext: string;
        }) => {
            const response = hittingPostApi('/ticket/purchase/apple-pay-auth', params);
            return response as unknown as PurchaseResponse;
        },
        [hittingPostApi],
    );

    /**
     * ブラウザ変更API
     * @param {Object} params -put request parameters
     * @param {string} params.orderTicketId -(required)子券ID
     * @param {number} params.itemNumber -(required)券片番号
     * @param {string} params.deviceId -(required)有効化端末ID
     */
    const changeTicketBrowser = useCallback(
        async (params: { orderTicketId: string; itemNumber?: number; deviceId: string | null }) =>
            hittingPutApi('/order/device-id-activate', params),
        [hittingPutApi],
    );

    /**
     * ApplePay払い戻しAPI
     * @param {Object} params -post request parameters
     * @param {string} params.orderId -(required) 払戻し対象の企画券ID
     * @param {string} params.cardToken -カード情報を暗号化した
     */
    const refundApplePayVisa = useCallback(
        async (params: { orderId: string; cardToken?: string }) => {
            const response = await hittingPostApi('/order/refund/applepay-visa', params);
            return response as unknown as { message: string[] };
        },
        [hittingPostApi],
    );
    /**
     * 予約カレンダー取得API
     * @param {Object} params -get request parameters
     * @param {number} params.ticketId -(required)オーダーID
     * @param {number} params.year -(required) 年
     * @param {number} params.month -(required) 月
     * @param {number} timeout - request timeout duration in milliseconds
     * @returns
     */
    const fetchReservationCalendar = useCallback(
        async (
            params: {
                saleCombinationTicketId: string;
                year: number;
                month: number;
                startTargetReserveOn: Date | null;
                endTargetReserveOn: Date | null;
            },
            timeout: number,
        ) => {
            const response = await hittingGetApi('reservation/calendar', params, timeout);
            return response as unknown as Calendar;
        },
        [hittingGetApi],
    );

    /**
     * 在庫一覧取得API
     * @param {Object} params -get request parameters
     * @param {number} params.ticketId -(required)オーダーID
     * @param {number} params.year -(required) 年
     * @param {number} params.month -(required) 月
     * @param {number} params.day -(required) 日
     * @param {number} timeout - request timeout duration in milliseconds
     * @returns
     */
    const fetchReservationTicketList = useCallback(
        async (
            params: {
                saleCombinationTicketId: string;
                year: number;
                month: number;
                day: number;
                startTargetReserveOn: Date | null;
                endTargetReserveOn: Date | null;
            },
            timeout: number,
        ) => {
            const response = await hittingGetApi('/stocks', params, timeout);
            return response as unknown as Stocks;
        },
        [hittingGetApi],
    );

    /**
     * 休日取得API
     * @param {number} timeout - request timeout duration in milliseconds
     */
    const fetchHolidays = useCallback(
        async (timeout?: number) => {
            const response = await hittingGetApi('/holidays', {}, timeout);
            return response as unknown as Holidays[];
        },
        [hittingGetApi],
    );
    /**
     * 多区間一覧取得API
     * @param {Object} params -get request parameters
     * @param {string} params.stockId -(required)stockId
     * @returns
     */
    const fetchStopList = useCallback(
        async (params: { stockId: number }) => {
            const response = await hittingGetApi('/reservation-prices', params);
            return response as unknown as Stops;
        },
        [hittingGetApi],
    );
    /**,
     * 無償チケット受取API
     * @param {Object} params -get request parameters
     * @param {string} params.salesTicketId -(required)販売券種ID
     * @param {string} params.lang -(required)言語コード
     * @param {number} params.numberAdult -人数(大人)
     * @param {number} params.countCustom1 -人数(カスタム１)
     * @param {number} params.countCustom2 -人数(カスタム２)
     * @param {number} params.countCustom3 -人数(カスタム３)
     * @param {Object} params.anonymousFormAnswers -匿名アンケートの回答
     * @param {Object} params.userForm -利用者情報の回答
     * @param {Object} params.reservationCombo -予約ありの個別チケットIDと在庫IDの組み合わせ
     * @param {Object} params.voucherCode -バウチャーコード
     */
    const purchaseTicketWithNoCost = useCallback(
        async (params: {
            salesTicketId?: number;
            lang: string;
            numberAdult?: number;
            countCustom1?: number;
            countCustom2?: number;
            countCustom3?: number;
            anonymousFormAnswers?: any;
            userForm?: any;
            reservationCombo?: Array<any>;
            voucherCode?: string;
        }) => {
            const response = await hittingPostApi('/ticket/purchase/no-cost', params);
            return response as unknown as PurchaseResponse;
        },
        [hittingPostApi],
    );

    /**
     * バウチャーコード検証API
     * @param {Object} params -get request parameters
     * @param {string} params.salesTicketId -(required)販売券種ID
     * @param {number} params.voucherCode -(required) 「一時的なバウチャーコード」、「固定バウチャーコード」
     * @returns
     */
    const validateVoucherCode = useCallback(
        async (params: { salesTicketId: number; voucherCode: number }) =>
            hittingGetApi('/ticket/voucher-code/validate', params),
        [hittingGetApi],
    );

    /**
     * 分配するAPI
     * @param {Object} params -get request parameters
     * @param {string} params.orderTicketId -(required)親オーダーID
     * @param {Object} params.distributeCount -(required) 分配数
     * @returns
     */
    const distributionsTicket = useCallback(
        async (params: { orderTicketId: string; distributeNumber?: number }, timeout: number) => {
            const response = await hittingPostApi('/distributions', params, timeout);
            return response as unknown as { orderTicketId: string };
        },
        [hittingPostApi],
    );

    /**
     * 分配するAPI
     * @param {Object} params -get request parameters
     * @param {string} params.orderId -(required)親オーダーID
     * @param {Object} params.distributeCount -(required) 分配数
     * @returns
     */
    const fetchDistributionsInfo = useCallback(
        async (params: { orderTicketId: string; distributeCount?: SelectNumber }) => {
            const response = await hittingGetApi('/distributions', params);
            return response as unknown as Distributions[];
        },
        [hittingGetApi],
    );

    /**
     * 予約変更API
     * @param {Object} params -put request parameters
     * @param {string} params.reservationId -(optional) 予約ID
     * @param {number} params.stockId -(optional) 在庫ID
     * @param {number} params.originStopId -(optional) 出発停留所ID
     * @param {number} params.destStopId -(optional) 到着停留所ID
     * @param {string} params.reservationStatus -(optional) 予約状態
     * @param {Object} params.userForm -(optional) 予約者情報
     */
    const reservationUpdate = useCallback(
        async (params: {
            reservationId?: string;
            stockId?: string;
            originStopId?: string;
            destStopId?: string;
            reservationStatus?: string;
            userForm?: any;
        }) => {
            const response = await hittingPutApi('/reservation', params);
            return response as unknown as { message: string };
        },
        [hittingPutApi],
    );

    /**
     * QRコード取得API
     * @param {Object} params -get request parameters
     * @param {string} params.remainingOrderDistributionId -(required)分配チケット残りID
     * @returns
     */
    const fetchQrCode = useCallback(
        async (
            params: {
                remainingOrderDistributionId?: string | null;
                orderDistributionId?: string | null;
                orderTicketId?: string | null;
            },
            timeout: number,
        ) => {
            const response = await hittingGetApi('/qr-code', params, timeout);
            return response as unknown as { qrCode: string };
        },
        [hittingGetApi],
    );

    /**
     * ユーザ情報削除API
     * @param {Object} params -request parameters
     * @param {number} timeout - request timeout duration in milliseconds
     * @returns
     */
    const deleteUser = useCallback(
        async (params: any, timeout: number) => hittingDeleteApi('/user', params, timeout),
        [hittingDeleteApi],
    );

    /**
     * QRコード取得API
     * @param {Object} params -get request parameters
     * @param {string} params.distributionCode -(required)分配用コード
     * @returns
     */
    const fetchDistributionQrCode = useCallback(
        async (params: { distributionCode: string }, timeout: number) => {
            const response = await hittingGetApi('/distribution', params, timeout);
            return response as unknown as DistributionQrCode;
        },
        [hittingGetApi],
    );

    /**,
     * チケット受け取りAPI
     * @param {Object} params -post request parameters
     * @param {string} params.orderDistributionId -(required)分配チケットID
     * @param {string} params.distributionCode -(required)分配用コード
     */
    const receiveTicket = useCallback(
        async (params: { orderDistributionId?: string; distributionCode?: string }, timeout: number) => {
            const response = await hittingPostApi('/receive-order', params, timeout);
            return response as unknown as { message: string };
        },
        [hittingPostApi],
    );

    /**
     * チケット使用終了API
     * @param {Object} params -put request parameters
     * @param {string} params.orderTicketId -(required) 子券ID
     * @param {number} timeout
     */
    const terminateTicket = useCallback(
        async (params: { orderTicketId: string }, timeout?: number) =>
            hittingPutApi('order/status/terminate', params, timeout),
        [hittingPutApi],
    );

    return {
        loading,
        fetchTicketList,
        fetchTicketInfo,
        purchaseTicket,
        fetchOrderList,
        fetchOrderDetail,
        fetchGroupTicketList,
        validateSpot,
        activateTicket,
        usageSpotTicket,
        refundTicket,
        fetchAppConfig,
        purchaseTicketWithCard,
        purchaseTicketWithCardComplete,
        purchaseTicketWithPayPay,
        changeTicketBrowser,
        refundApplePayVisa,
        fetchReservationCalendar,
        fetchReservationTicketList,
        fetchHolidays,
        fetchStopList,
        purchaseTicketWithNoCost,
        validateVoucherCode,
        distributionsTicket,
        fetchDistributionsInfo,
        reservationUpdate,
        fetchQrCode,
        deleteUser,
        fetchDistributionQrCode,
        receiveTicket,
        applePayAuth,
        terminateTicket,
    };
};

export default useR05Api;
